import React, { useCallback, useEffect, useRef } from 'react';
import { Spin } from 'antd';
import { ChatMessage, Conversation } from '@nxsys/store/slices/message/types';
import ChatBubble from './ChatBubble';

type ChatMessageListProps = {
  messages: ChatMessage[];
  currentUserId: string;
  conversation: Conversation;
  currentUser: any;
  isBureau: boolean;
  messagesLoading: boolean;
  hasMoreMessages: boolean;
  maxOtherReadAt: string | null;
  onLoadMoreMessages: () => void;
};

const ChatMessageList: React.FC<ChatMessageListProps> = ({
  messages,
  currentUserId,
  conversation,
  currentUser,
  isBureau,
  messagesLoading,
  hasMoreMessages,
  maxOtherReadAt,
  onLoadMoreMessages,
}) => {
  const conversationId = conversation.id;
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollableRef = useRef<HTMLDivElement>(null);
  const prevMessagesLenRef = useRef<number>(0);
  const prevScrollHeightRef = useRef<number>(0);
  const isLoadingOlderRef = useRef(false);

  // Handle scroll after messages change
  useEffect(() => {
    const container = scrollableRef.current;
    if (!container || messages.length === 0) return;

    if (isLoadingOlderRef.current) {
      const newScrollHeight = container.scrollHeight;
      container.scrollTop = newScrollHeight - prevScrollHeightRef.current;
      isLoadingOlderRef.current = false;
    } else if (
      prevMessagesLenRef.current === 0 ||
      messages.length > prevMessagesLenRef.current
    ) {
      const isNearBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight <
        150;
      if (prevMessagesLenRef.current === 0 || isNearBottom) {
        messagesEndRef.current?.scrollIntoView();
      }
    }

    prevMessagesLenRef.current = messages.length;
  }, [messages]);

  // Reset on conversation change
  useEffect(() => {
    prevMessagesLenRef.current = 0;
    isLoadingOlderRef.current = false;
  }, [conversationId]);

  // Scroll to top → load older messages
  const handleScroll = useCallback(() => {
    const container = scrollableRef.current;
    if (
      !container ||
      !hasMoreMessages ||
      messagesLoading ||
      isLoadingOlderRef.current
    )
      return;
    if (container.scrollTop <= 50) {
      isLoadingOlderRef.current = true;
      prevScrollHeightRef.current = container.scrollHeight;
      onLoadMoreMessages();
    }
  }, [hasMoreMessages, messagesLoading, onLoadMoreMessages]);

  return (
    <div className="chat-messages" ref={scrollableRef} onScroll={handleScroll}>
      {messagesLoading && messages.length > 0 && (
        <div style={{ textAlign: 'center', padding: 8 }}>
          <Spin size="small" />
        </div>
      )}

      {messagesLoading && messages.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: 20,
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Spin />
        </div>
      ) : (
        messages.map((msg) => (
          <ChatBubble
            key={msg._tempId || msg.id}
            message={msg}
            isMe={String(msg.senderUserId) === String(currentUserId)}
            maxOtherReadAt={maxOtherReadAt}
            conversation={conversation}
            currentUser={currentUser}
            isBureau={isBureau}
          />
        ))
      )}
      <div ref={messagesEndRef} />
    </div>
  );
};

export default ChatMessageList;
